import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import {
  attachWorkerKycPhoto,
  getWorkerKyc,
  removeWorkerKycPhoto,
  saveWorkerKyc,
  workerKycPhotoUploadUrl,
} from "../../../services/karkhanaApi";
import { confirmDialog } from "../ui/dialog";
import { useKT } from "../i18n/kt";
import { aadhaarInput, aadhaarToSave, IFSC, KYC_PHOTOS, KycPhotoKind, normalizeIfsc, photoFileType } from "./kyc";

type Kyc = {
  accountHolderName?: string | null;
  bankName?: string | null;
  accountNumber?: string | null;
  ifsc?: string | null;
  upiId?: string | null;
  aadhaarMasked?: string | null;
  photos?: Partial<Record<KycPhotoKind, { url?: string | null } | null>>;
};

const emptyForm = (k?: Kyc) => ({
  accountHolderName: k?.accountHolderName || "",
  bankName: k?.bankName || "",
  accountNumber: k?.accountNumber || "",
  ifsc: k?.ifsc || "",
  upiId: k?.upiId || "",
  aadhaar: k?.aadhaarMasked || "",
});

/**
 * Bank details and Aadhaar for paying a worker. Studio admin only (the server refuses everyone
 * else). Only the last 4 Aadhaar digits are kept; photos are private and shown through links that
 * work for a few minutes.
 */
const WorkerKycSection = ({ workerId }: { workerId: string }) => {
  const { t } = useKT();
  const [loading, setLoading] = useState(true);
  const [kyc, setKyc] = useState<Kyc>({});
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyPhoto, setBusyPhoto] = useState<KycPhotoKind | null>(null);
  const files = useRef<Partial<Record<KycPhotoKind, HTMLInputElement | null>>>({});

  const apply = (data: Kyc) => {
    setKyc(data || {});
    setForm(emptyForm(data));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await getWorkerKyc(workerId);
      if (res?.success) apply(res.data);
      else setError(res?.message || t("Could not load bank details"));
    } catch {
      setError(t("Could not load bank details"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const ifsc = normalizeIfsc(form.ifsc);
    if (ifsc && !IFSC.test(ifsc)) {
      setError(t("Check the IFSC: 11 characters like SBIN0001234"));
      return;
    }
    const aadhaar = aadhaarToSave(form.aadhaar);
    if (aadhaar === null) {
      setError(t("Enter all 12 Aadhaar digits"));
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res: any = await saveWorkerKyc(workerId, {
        accountHolderName: form.accountHolderName.trim(),
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.replace(/[\s-]/g, ""),
        ifsc,
        upiId: form.upiId.trim(),
        aadhaar,
      });
      if (!res?.success) {
        setError(res?.message || t("Could not save bank details"));
        return;
      }
      apply(res.data);
      notifications({ title: t("Saved"), message: t("Bank and Aadhaar details saved"), type: "success" });
    } catch {
      setError(t("Could not reach the server"));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (kind: KycPhotoKind, file?: File | null) => {
    if (!file) return;
    const fileType = photoFileType(file.type);
    if (!fileType) {
      notifications({ title: "Error", message: t("Choose a JPEG or PNG photo"), type: "danger" });
      return;
    }
    setBusyPhoto(kind);
    try {
      const res: any = await workerKycPhotoUploadUrl(workerId, kind, fileType);
      if (!res?.success || !res?.data?.uploadUrl) throw new Error(res?.message);
      const put = await fetch(res.data.uploadUrl, { method: "PUT", headers: { "Content-Type": fileType }, body: file });
      if (!put.ok) throw new Error();
      const saved: any = await attachWorkerKycPhoto(workerId, kind, res.data.key);
      if (!saved?.success) throw new Error(saved?.message);
      setKyc(saved.data || {});
    } catch (e: any) {
      notifications({ title: "Error", message: e?.message || t("The photo didn't upload. Try again"), type: "danger" });
    } finally {
      setBusyPhoto(null);
      const input = files.current[kind];
      if (input) input.value = "";
    }
  };

  const remove = async (kind: KycPhotoKind, label: string) => {
    const ok = await confirmDialog({ title: t("Remove {{what}}?", { what: t(label) }), ok: t("Remove"), danger: true });
    if (!ok) return;
    setBusyPhoto(kind);
    try {
      const res: any = await removeWorkerKycPhoto(workerId, kind);
      if (!res?.success) throw new Error(res?.message);
      setKyc(res.data || {});
    } catch (e: any) {
      notifications({ title: "Error", message: e?.message || t("Could not remove the photo"), type: "danger" });
    } finally {
      setBusyPhoto(null);
    }
  };

  if (loading) return <p className="kw-muted mt-3">{t("Loading bank details…")}</p>;

  return (
    <section className="kw-kyc" aria-labelledby="kw-kyc-title">
      <h3 id="kw-kyc-title" className="kw-kyc__title">
        {t("Bank & Aadhaar")}
      </h3>
      <p className="kw-muted">{t("Only the studio admin sees these. Only the last 4 Aadhaar digits are kept.")}</p>

      <Label for="kw-kyc-holder">{t("Account holder name")}</Label>
      <Input
        id="kw-kyc-holder"
        value={form.accountHolderName}
        autoComplete="off"
        onChange={(e) => setForm({ ...form, accountHolderName: e.target.value })}
      />
      <Label className="mt-2" for="kw-kyc-bank">
        {t("Bank name")}
      </Label>
      <Input id="kw-kyc-bank" value={form.bankName} autoComplete="off" onChange={(e) => setForm({ ...form, bankName: e.target.value })} />
      <Label className="mt-2" for="kw-kyc-account">
        {t("Account number")}
      </Label>
      <Input
        id="kw-kyc-account"
        className="kw-code"
        inputMode="numeric"
        autoComplete="off"
        value={form.accountNumber}
        onChange={(e) => setForm({ ...form, accountNumber: e.target.value.replace(/[^\d\s-]/g, "") })}
      />
      <div className="kw-kyc__pair">
        <div>
          <Label className="mt-2" for="kw-kyc-ifsc">
            IFSC
          </Label>
          <Input
            id="kw-kyc-ifsc"
            className="kw-code"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={11}
            placeholder="SBIN0001234"
            value={form.ifsc}
            onChange={(e) => setForm({ ...form, ifsc: normalizeIfsc(e.target.value) })}
          />
        </div>
        <div>
          <Label className="mt-2" for="kw-kyc-upi">
            {t("UPI ID (optional)")}
          </Label>
          <Input
            id="kw-kyc-upi"
            autoCapitalize="none"
            autoComplete="off"
            placeholder="name@bank"
            value={form.upiId}
            onChange={(e) => setForm({ ...form, upiId: e.target.value })}
          />
        </div>
      </div>
      <Label className="mt-2" for="kw-kyc-aadhaar">
        {t("Aadhaar number")}
      </Label>
      <Input
        id="kw-kyc-aadhaar"
        className="kw-code"
        inputMode="numeric"
        autoComplete="off"
        placeholder="1234 5678 9012"
        value={form.aadhaar}
        onChange={(e) => setForm({ ...form, aadhaar: aadhaarInput(e.target.value) })}
      />
      <p className="kw-muted mt-1">{t("Type all 12 digits; HKP keeps only the last 4.")}</p>

      {error ? (
        <p className="kw-kyc__error" role="alert">
          {error}
        </p>
      ) : null}
      <Button className="ResetBtn mt-2" disabled={saving} onClick={save}>
        {saving ? t("Saving…") : t("Save bank details")}
      </Button>

      <div className="kw-kyc__photos">
        {KYC_PHOTOS.map(({ kind, label }) => {
          const url = kyc.photos?.[kind]?.url;
          const has = !!kyc.photos?.[kind];
          const busy = busyPhoto === kind;
          return (
            <div key={kind} className="kw-kyc__photo">
              <span className="kw-kyc__photo-label">{t(label)}</span>
              {has ? (
                url ? (
                  <a href={url} target="_blank" rel="noreferrer" className="kw-kyc__thumb">
                    <img src={url} alt={t(label)} />
                  </a>
                ) : (
                  <span className="kw-kyc__thumb is-empty">{t("Saved")}</span>
                )
              ) : (
                <span className="kw-kyc__thumb is-empty">{t("No photo")}</span>
              )}
              <input
                ref={(el) => {
                  files.current[kind] = el;
                }}
                type="file"
                accept="image/jpeg,image/png,image/heic"
                hidden
                onChange={(e) => upload(kind, e.target.files?.[0])}
              />
              <span className="kw-kyc__photo-actions">
                <Button size="sm" outline disabled={busy} onClick={() => files.current[kind]?.click()}>
                  {busy ? t("Uploading…") : has ? t("Replace") : t("Add photo")}
                </Button>
                {has ? (
                  <Button size="sm" outline color="secondary" disabled={busy} onClick={() => remove(kind, label)}>
                    {t("Remove")}
                  </Button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default WorkerKycSection;
